/**
 * index.tsx — Application entry point.
 *
 * This is the first file executed by the browser. Its sole job is to mount the
 * React application onto the HTML page. The actual `<div id="root">` element it
 * targets lives in public/index.html.
 *
 * Providers wrapped here are available to every component in the app:
 *   - React.StrictMode  — enables extra runtime warnings in development to
 *                         surface potential problems early (has no effect in prod).
 *   - HelmetProvider    — required by react-helmet-async so each page can update
 *                         the <title> and <meta> tags for SEO.
 *
 * The i18n import (no default export used) is a side-effect import — importing
 * the file runs the i18next initialization code in utils/i18n.ts, making
 * translations available to all components before the app renders.
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import { HelmetProvider } from 'react-helmet-async';
import './index.css';       // Global base styles (CSS variables, font, body reset)
import App from './App';
import reportWebVitals from './reportWebVitals';
import './utils/i18n';      // Side-effect: initializes i18next translation system

// Attach the React app to the <div id="root"> in public/index.html.
// createRoot is the React 18+ API that enables concurrent rendering features.
const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);
root.render(
  <React.StrictMode>
    {/* HelmetProvider manages <head> tag updates (title, meta) across all pages */}
    <HelmetProvider>
      <App />
    </HelmetProvider>
  </React.StrictMode>
);

// reportWebVitals measures real-user performance metrics (CLS, FID, FCP, LCP, TTFB).
// Pass a callback (e.g. console.log or an analytics function) to receive the data.
// Called with no arguments here, so metrics are collected but not reported anywhere.
reportWebVitals();
